import {
  GltfRangeRequestError,
  InvalidGltfManifestError,
  throwIfMeshLoadCancelled,
} from '@/plugins/mesh/errors'
import type { MeshLoadRequest } from '@/plugins/mesh/types'

/** 预检读取额度不足；图片探测可将此错误降级为未知尺寸。 */
export class GltfPreflightReadLimitError extends Error {}

/** 按需读取元数据与图片头，遵守调用方额度，不创建位图或读取完整 BIN。 */
export class GltfPreflightReader {
  private _readBytes = 0
  private readonly _maxBytes: number

  constructor(private readonly _request: MeshLoadRequest) {
    this._maxBytes = _request.gltf?.budget?.maxPreflightBytes ?? Infinity
    if (this._maxBytes !== Infinity && (!Number.isFinite(this._maxBytes) || this._maxBytes < 0))
      throw new RangeError('预检预算必须是非负字节数或 Infinity')
  }

  get readBytes(): number {
    return this._readBytes
  }

  /** 完整读取 glTF JSON 元数据；容量由调用方配置，GLB BIN 不走此路径。 */
  async readAll(source: Blob | ArrayBuffer | ArrayBufferView | string): Promise<Uint8Array> {
    const signal = this._request.signal
    throwIfMeshLoadCancelled(signal, this._request.url ?? '(预检)')
    if (typeof source !== 'string') {
      const length = source instanceof Blob ? source.size : source.byteLength
      return length === 0 ? new Uint8Array() : this.read(source, 0, length)
    }
    const headers = new Headers(this._request.headers)
    headers.delete('Range')
    let response: Response
    try {
      response = await fetch(source, { headers, signal, credentials: this._request.credentials })
    } catch (error) {
      throwIfMeshLoadCancelled(signal, source)
      throw error
    }
    if (response.status !== 200 || !response.body) {
      await response.body?.cancel()
      throw new InvalidGltfManifestError(source, '无法完整读取 glTF JSON 元数据')
    }
    const declaredLength = Number(response.headers.get('content-length'))
    if (Number.isFinite(declaredLength) && this._readBytes + declaredLength > this._maxBytes) {
      await response.body.cancel()
      throw new GltfPreflightReadLimitError('glTF JSON 超过调用方预检额度')
    }
    const reader = response.body.getReader()
    const chunks: Uint8Array[] = []
    let length = 0
    try {
      while (true) {
        throwIfMeshLoadCancelled(signal, source)
        const { done, value } = await reader.read()
        if (done) break
        this._readBytes += value.byteLength
        if (this._readBytes > this._maxBytes)
          throw new GltfPreflightReadLimitError('glTF JSON 超过调用方预检额度')
        chunks.push(value)
        length += value.byteLength
      }
    } catch (error) {
      throwIfMeshLoadCancelled(signal, source)
      throw error
    } finally {
      await reader.cancel().catch(() => undefined)
      reader.releaseLock()
    }
    throwIfMeshLoadCancelled(signal, source)
    const result = new Uint8Array(length)
    let offset = 0
    for (const chunk of chunks) {
      result.set(chunk, offset)
      offset += chunk.byteLength
    }
    return result
  }

  async read(
    source: Blob | ArrayBuffer | ArrayBufferView | string,
    offset: number,
    length: number,
    allowShort = false
  ): Promise<Uint8Array> {
    const signal = this._request.signal
    throwIfMeshLoadCancelled(signal, this._request.url ?? '(预检)')
    if (
      !Number.isSafeInteger(offset) ||
      offset < 0 ||
      !Number.isSafeInteger(length) ||
      length <= 0 ||
      this._readBytes + length > this._maxBytes
    ) {
      throw new GltfPreflightReadLimitError('glTF 预检范围无效或超过调用方额度')
    }
    if (typeof source !== 'string') {
      const bytes =
        source instanceof Blob
          ? new Uint8Array(await source.slice(offset, offset + length).arrayBuffer())
          : ArrayBuffer.isView(source)
            ? new Uint8Array(source.buffer, source.byteOffset, source.byteLength).subarray(
                offset,
                offset + length
              )
            : new Uint8Array(source).subarray(offset, offset + length)
      this._readBytes += bytes.byteLength
      throwIfMeshLoadCancelled(signal, this._request.url ?? '(预检)')
      if (!allowShort && bytes.byteLength !== length) {
        throw new InvalidGltfManifestError('(预检)', '元数据读取越界')
      }
      return bytes
    }
    const headers = new Headers(this._request.headers)
    headers.set('Range', `bytes=${offset}-${offset + length - 1}`)
    let response: Response
    try {
      response = await fetch(source, {
        headers,
        signal,
        credentials: this._request.credentials,
      })
    } catch (error) {
      throwIfMeshLoadCancelled(signal, source)
      throw error
    }
    if (response.status !== 200 && response.status !== 206) {
      await response.body?.cancel()
      throw new GltfRangeRequestError(source, 'range-unavailable', `HTTP ${response.status}`)
    }
    let responseLength: number | undefined
    if (response.status === 206) {
      const match = /^bytes (\d+)-(\d+)\/(\d+|\*)$/u.exec(
        response.headers.get('content-range') ?? ''
      )
      if (
        !match ||
        Number(match[1]) !== offset ||
        Number(match[2]) >= offset + length ||
        Number(match[2]) < offset ||
        (match[3] !== '*' && Number(match[2]) >= Number(match[3]))
      ) {
        await response.body?.cancel()
        throw new GltfRangeRequestError(source, 'range-invalid-response', '预检 Content-Range 无效')
      }
      responseLength = Number(match[2]) - offset + 1
    }
    const skip = response.status === 200 ? offset : 0
    if (this._readBytes + skip + length > this._maxBytes) {
      await response.body?.cancel()
      throw new GltfPreflightReadLimitError('Range 不可用，目标位置超过预检读取额度')
    }
    if (!response.body) {
      throw new GltfPreflightReadLimitError('响应不支持有界流式读取')
    }
    const reader = response.body.getReader()
    const output = new Uint8Array(length)
    let consumed = 0
    let written = 0
    try {
      while (written < length) {
        throwIfMeshLoadCancelled(signal, source)
        const { done, value } = await reader.read()
        if (done) break
        this._readBytes += value.byteLength
        if (this._readBytes > this._maxBytes) {
          throw new GltfPreflightReadLimitError('响应块超过预检读取额度')
        }
        const start = Math.max(0, skip - consumed)
        const count = Math.min(Math.max(0, value.byteLength - start), length - written)
        output.set(value.subarray(start, start + count), written)
        written += count
        consumed += value.byteLength
      }
    } catch (error) {
      throwIfMeshLoadCancelled(signal, source)
      throw error
    } finally {
      await reader.cancel().catch(() => undefined)
      reader.releaseLock()
    }
    throwIfMeshLoadCancelled(signal, source)
    if (responseLength !== undefined && (written !== responseLength || consumed > responseLength)) {
      throw new GltfRangeRequestError(
        source,
        'range-invalid-response',
        '预检响应体与 Content-Range 长度不一致'
      )
    }
    if (!allowShort && written !== length) {
      throw new InvalidGltfManifestError(source, '预检响应短于请求长度')
    }
    return written === length ? output : output.slice(0, written)
  }
}
